/* Survey runner — teacher call list + answer form. Used by staff and by the
 * admin "Survey" shortcut (/surveys/run/:sid?teacher=<id>). */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Phone, Search, CheckCircle2, ArrowLeft, Send, Star } from "lucide-react";

export default function SurveyRun() {
  const { sid } = useParams();
  const [sp] = useSearchParams();
  const [survey, setSurvey] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState({});
  const [overallNote, setOverallNote] = useState("");
  const [saving, setSaving] = useState(false);

  const loadTeachers = () => api.get(`/surveys/${sid}/teachers`).then(({ data }) => setTeachers(data.teachers || [])).catch(() => {});
  useEffect(() => {
    api.get(`/surveys/${sid}`).then(({ data }) => setSurvey(data.survey)).catch(() => toast.error("Survey not found"));
    loadTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  useEffect(() => {
    const pre = sp.get("teacher");
    if (pre && teachers.length && !target) {
      const t = teachers.find((x) => x.id === pre);
      if (t) setTarget(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teachers]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return teachers.filter((t) => !s || `${t.full_name} ${t.email} ${t.phone}`.toLowerCase().includes(s));
  }, [teachers, q]);

  const submit = async () => {
    if (!target) return;
    setSaving(true);
    try {
      await api.post(`/surveys/${sid}/responses`, {
        teacher_id: target.id,
        answers: (survey?.questions || []).map((qu) => ({ question_id: qu.id, value: answers[qu.id] ?? "", note: notes[qu.id] ?? "" })),
        overall_note: overallNote,
      });
      toast.success(`Response saved for ${target.full_name}`);
      setTarget(null); setAnswers({}); setNotes({}); setOverallNote("");
      loadTeachers();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save response"); }
    setSaving(false);
  };

  if (!survey) return <div className="text-neutral-500 text-sm">Loading survey…</div>;

  return (
    <div className="space-y-5" data-testid="survey-run-page">
      <div>
        <button onClick={() => window.history.back()} className="text-xs text-neutral-500 hover:text-[#0A0C10] inline-flex items-center gap-1 mb-2" data-testid="survey-run-back">
          <ArrowLeft size={12} /> Back
        </button>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ run survey</div>
        <h1 className="font-display font-bold text-2xl tracking-tighter">{survey.title}</h1>
        {survey.description && <p className="text-sm text-neutral-600 mt-1">{survey.description}</p>}
      </div>

      <div className="grid lg:grid-cols-[340px,1fr] gap-5 items-start">
        {/* Teacher call list */}
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="p-3 border-b border-neutral-100">
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search teachers…" data-testid="survey-teacher-search"
                className="w-full pl-8 pr-3 py-2 text-sm border border-neutral-200 rounded-md outline-none focus:border-[#0A0C10]" />
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-neutral-100">
            {filtered.map((t) => (
              <button key={t.id} onClick={() => setTarget(t)} data-testid={`survey-teacher-${t.id}`}
                className={`w-full text-left px-4 py-2.5 hover:bg-neutral-50 transition-colors ${target?.id === t.id ? "bg-[#0A0C10]/5 border-l-2 border-[#0A0C10]" : ""}`}>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm truncate">{t.full_name || t.email}</span>
                  {t.surveyed && <CheckCircle2 size={13} className="text-emerald-500 shrink-0" title="Already surveyed" />}
                </div>
                <div className="text-xs text-neutral-500 font-mono inline-flex items-center gap-1.5">
                  <Phone size={10} /> {t.phone || "no phone"} · {t.email}
                </div>
              </button>
            ))}
            {filtered.length === 0 && <div className="px-4 py-8 text-center text-xs text-neutral-500">No teachers match.</div>}
          </div>
        </div>

        {/* Answer form */}
        {!target ? (
          <div className="bg-white border border-dashed border-black/15 rounded-xl p-12 text-center text-sm text-neutral-500" data-testid="survey-pick-hint">
            Pick a teacher from the list, dial their number and note the answers here.
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-5" data-testid="survey-answer-form">
            <div className="flex flex-wrap items-center gap-3 pb-4 border-b border-neutral-100">
              <div>
                <div className="font-display font-bold text-lg">{target.full_name}</div>
                <div className="text-xs text-neutral-500 font-mono">{target.email}</div>
              </div>
              <a href={`tel:${target.phone}`} className="ml-auto inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700" data-testid="survey-call-btn">
                <Phone size={14} /> {target.phone || "No phone"}
              </a>
            </div>
            {(survey.questions || []).map((qu, i) => (
              <div key={qu.id} className="space-y-2" data-testid={`survey-q-${qu.id}`}>
                <div className="text-sm font-semibold">{i + 1}. {qu.text}</div>
                {qu.type === "text" && (
                  <textarea rows={2} value={answers[qu.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [qu.id]: e.target.value }))}
                    data-testid={`survey-answer-${qu.id}`}
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0A0C10]" placeholder="Teacher's answer…" />
                )}
                {qu.type === "yesno" && (
                  <div className="flex gap-2">
                    {["Yes", "No"].map((v) => (
                      <button key={v} onClick={() => setAnswers((a) => ({ ...a, [qu.id]: v }))} data-testid={`survey-answer-${qu.id}-${v.toLowerCase()}`}
                        className={`px-5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${answers[qu.id] === v ? "bg-[#0A0C10] text-white border-transparent" : "border-neutral-300 hover:border-[#0A0C10]"}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                )}
                {qu.type === "rating" && (
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} onClick={() => setAnswers((a) => ({ ...a, [qu.id]: String(n) }))} data-testid={`survey-answer-${qu.id}-${n}`}
                        className={`w-9 h-9 rounded-md border text-sm font-bold transition-colors inline-flex items-center justify-center ${Number(answers[qu.id]) >= n ? "bg-[#FFB400] border-transparent text-[#0A0C10]" : "border-neutral-300 text-neutral-400 hover:border-[#FFB400]"}`}>
                        <Star size={14} className={Number(answers[qu.id]) >= n ? "fill-[#0A0C10]" : ""} />
                      </button>
                    ))}
                    <span className="self-center text-xs text-neutral-500 ml-1">{answers[qu.id] ? `${answers[qu.id]}/5` : ""}</span>
                  </div>
                )}
                {qu.type === "choice" && (
                  <div className="flex flex-wrap gap-2">
                    {(qu.options || []).map((o) => (
                      <button key={o} onClick={() => setAnswers((a) => ({ ...a, [qu.id]: o }))} data-testid={`survey-answer-${qu.id}-choice`}
                        className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${answers[qu.id] === o ? "bg-[#0A0C10] text-white border-transparent" : "border-neutral-300 hover:border-[#0A0C10]"}`}>
                        {o}
                      </button>
                    ))}
                  </div>
                )}
                <input value={notes[qu.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [qu.id]: e.target.value }))}
                  data-testid={`survey-note-${qu.id}`}
                  placeholder="Extra notes for this question (optional)…"
                  className="w-full border border-dashed border-neutral-300 rounded-md px-3 py-1.5 text-xs outline-none focus:border-[#0A0C10] text-neutral-600" />
              </div>
            ))}
            <div>
              <div className="text-sm font-semibold mb-1.5">Overall call notes</div>
              <textarea rows={3} value={overallNote} onChange={(e) => setOverallNote(e.target.value)} data-testid="survey-overall-note"
                className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0A0C10]" placeholder="Anything else from the call…" />
            </div>
            <button onClick={submit} disabled={saving} data-testid="survey-submit-btn"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-[#0A0C10] text-white font-bold text-sm hover:bg-black disabled:opacity-60">
              <Send size={14} /> {saving ? "Saving…" : "Save response"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
